import { memo, useCallback, useMemo, useState, type ReactNode } from 'react';
import { IconButton, Paper, Typography } from '@mui/material';
import { Stack } from '@mui/system';
import { useTranslation } from 'react-i18next';

import { ToolbarItem, ToolbarItemType, ToolbarMenuItem, ToolId } from '../types';
import { getMenuIcon, getNodeKey, hasSelectedTool } from '../helpers';
import { styles } from '../styles';
import { Icon } from '../Icon';

type Props = {
  menu: ToolbarMenuItem;
  variant: ToolbarItemType;
  selectedToolId?: ToolId;
  lastSelectedToolId?: ToolId;
  disabled?: boolean;
  renderNode: (node: ToolbarItem, index: number) => ReactNode;
};

function HoverMenuComponent({
  menu,
  selectedToolId,
  lastSelectedToolId,
  variant = 'tool',
  disabled = false,
  renderNode,
}: Props) {
  const { t } = useTranslation(['interface']);
  const [isOpen, setIsOpen] = useState(false);

  const menuIcon = useMemo(
    () => getMenuIcon(menu, selectedToolId, lastSelectedToolId),
    [menu, selectedToolId, lastSelectedToolId],
  );
  const MenuIconComp = menuIcon ? <Icon icon={menuIcon} /> : null;
  const isSelectedMenu = hasSelectedTool(menu.items, selectedToolId);

  const handleMouseEnter = useCallback(() => {
    if (disabled) {
      return;
    }
    setIsOpen(true);
  }, [disabled]);

  const handleMouseLeave = useCallback(() => {
    setIsOpen(false);
  }, []);

  return (
    <Stack
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={styles.menuRoot}
      sx={{
        opacity: disabled ? 0.5 : 1,
        pointerEvents: disabled ? 'none' : 'auto',
      }}
    >
      <Stack
        borderRadius={1}
        sx={[
          variant === 'menu' ? styles.row : styles.toolButton,
          variant !== 'menu' && isSelectedMenu && styles.menuButtonSelected,
          disabled && {
            color: (theme) => theme.palette.action.disabled,
            backgroundColor: 'transparent',
          },
        ]}
      >
        <Stack style={styles.rowInner}>
          {MenuIconComp && (
            <IconButton
              color='inherit'
              style={styles.iconButton}
              tabIndex={-1}
              disableRipple
              disableFocusRipple
              disableTouchRipple
              sx={{
                marginLeft: variant === 'menu' ? -1 : 0,
                pointerEvents: 'none',
                color: 'inherit',
              }}
              size='medium'
            >
              {MenuIconComp}
            </IconButton>
          )}
          {variant === 'menu' && <Typography>{t(menu.label)}</Typography>}
        </Stack>
        {variant === 'menu' && <Typography>›</Typography>}
      </Stack>

      {isOpen && (
        <Stack style={styles.menuFlyoutArea} onMouseLeave={handleMouseLeave}>
          <Paper elevation={3} style={styles.menuPanel}>
            {menu.items.map((child, index) => (
              <Stack key={getNodeKey(child, index)} style={styles.menuItem}>
                {renderNode(child, index)}
              </Stack>
            ))}
          </Paper>
        </Stack>
      )}
    </Stack>
  );
}

export const HoverMenu = memo(HoverMenuComponent);
